import { useEffect, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { useMaintenanceStats, type LifecycleStageStat } from '@/hooks/maintenance/useMaintenanceStats';
import {
  LIFECYCLE_STAGE_LABELS,
  LIFECYCLE_WATCH_SUBSTATUS_OPTIONS,
  LIFECYCLE_WATCH_SUBSTATUS_COLORS,
  type LifecycleStage,
  type LifecycleWatchSubstatus,
} from '@/constants/lifecycleStages';
import { formatQty } from '@/utils/lifecycleQuantity';
import { cn } from '@/lib/utils';
import type { MaintenanceFiltersData } from '@/hooks/maintenance/useMaintenanceFiltersState';

interface Props {
  filters: MaintenanceFiltersData;
  activeStage?: LifecycleStage | '';
  onStageToggle?: (stage: LifecycleStage) => void;
  activeWatchSubstatus?: LifecycleWatchSubstatus | null;
  onWatchSubstatusToggle?: (sub: LifecycleWatchSubstatus, stage: LifecycleStage) => void;
}

const WATCH_CHIPS = LIFECYCLE_WATCH_SUBSTATUS_OPTIONS;

const STAGE_ORDER: LifecycleStage[] = ['yet_transferred', 'warranty', 'watch', 'maintenance'];

const STAGE_BORDER: Record<LifecycleStage, string> = {
  yet_transferred: 'border-l-gray-400',
  warranty: 'border-l-blue-500',
  watch: 'border-l-amber-500',
  maintenance: 'border-l-purple-500',
};

const STAGE_RING: Record<LifecycleStage, string> = {
  yet_transferred: 'ring-gray-400',
  warranty: 'ring-blue-500',
  watch: 'ring-amber-500',
  maintenance: 'ring-purple-500',
};

const buildStatQuantity = (stage: LifecycleStage, s: LifecycleStageStat): string => {
  if (stage === 'yet_transferred') return `${formatQty(s.sum_hour)} giờ`;
  if (stage === 'warranty' || stage === 'watch') return `${formatQty(s.sum_m2)} m²`;
  const hasH = s.sum_hour > 0;
  const hasM = s.sum_m2 > 0;
  if (hasH && hasM) return `${formatQty(s.sum_hour)} giờ / ${formatQty(s.sum_m2)} m²`;
  if (hasH) return `${formatQty(s.sum_hour)} giờ`;
  if (hasM) return `${formatQty(s.sum_m2)} m²`;
  return '—';
};

export default function MaintenanceStatsPanel({
  filters,
  activeStage = '',
  onStageToggle,
  activeWatchSubstatus = null,
  onWatchSubstatusToggle,
}: Props) {
  const { data, isLoading, isError } = useMaintenanceStats(filters);
  const toastedRef = useRef(false);

  useEffect(() => {
    if (isError && !toastedRef.current) {
      toastedRef.current = true;
      toast.error('Không thể tải thống kê dự án');
    }
    if (!isError) toastedRef.current = false;
  }, [isError]);

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {STAGE_ORDER.map((s) => (
          <Card key={s} className={`border-l-4 ${STAGE_BORDER[s]}`}>
            <CardContent className="p-4 space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-7 w-16" />
              <Skeleton className="h-3 w-20" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {STAGE_ORDER.map((stage) => {
        const stat: LifecycleStageStat =
          data?.[stage] ?? { count: 0, sum_hour: 0, sum_m2: 0 };
        const isActive = activeStage === stage;

        let displayCount = stat.count;
        let displayStat: LifecycleStageStat = stat;
        if (isActive && activeWatchSubstatus && stat.watch_substages) {
          const sub = stat.watch_substages[activeWatchSubstatus];
          displayCount = sub?.count ?? 0;
          displayStat = { ...stat, sum_m2: sub?.sum_m2 ?? 0 };
        }

        return (
          <Card
            key={stage}
            role="button"
            tabIndex={0}
            aria-pressed={isActive}
            onClick={() => onStageToggle?.(stage)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onStageToggle?.(stage);
              }
            }}
            className={cn(
              'border-l-4 cursor-pointer transition-colors select-none',
              'hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              STAGE_BORDER[stage],
              isActive && `ring-2 ring-offset-1 ${STAGE_RING[stage]} bg-muted/30`
            )}
          >
            <CardContent className="p-4">
              <div className="text-sm font-medium text-muted-foreground">
                {LIFECYCLE_STAGE_LABELS[stage]}
              </div>
              <div className="text-2xl font-bold mt-1">{displayCount} dự án</div>
              <div className="text-xs text-muted-foreground mt-1">
                {isError ? '—' : buildStatQuantity(stage, displayStat)}
              </div>

              <div
                className="flex flex-wrap gap-1 mt-2"
                onClick={(e) => e.stopPropagation()}
              >
                {WATCH_CHIPS.map((chip) => {
                  const count = stat.watch_substages?.[chip.value]?.count ?? 0;
                  const isChipActive = isActive && activeWatchSubstatus === chip.value;
                  const activeColor = LIFECYCLE_WATCH_SUBSTATUS_COLORS[chip.value];
                  return (
                    <button
                      key={chip.value}
                      type="button"
                      aria-pressed={isChipActive}
                      onClick={(e) => {
                        e.stopPropagation();
                        onWatchSubstatusToggle?.(chip.value, stage);
                      }}
                      className={cn(
                        'h-6 px-1.5 text-[10px] font-semibold rounded-md border transition-colors',
                        isChipActive
                          ? activeColor
                          : 'border-border text-muted-foreground hover:bg-muted/60'
                      )}
                    >
                      {chip.label} · {count}
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
